import React, { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import { actions as libActions } from '@ecosystem/newron-design-system';
import { BasicTrackingFn, Component, CoreContents, Params, SettingsProvider } from '../types';
import { useUiAction } from '../ui.actions';
import { DynamicTitleElement } from '../common/dynamic.title/title';
import { useTrackPageElementViewed } from '../analytics/trackPageElementViewed.hook';
import { useCorePageState } from '../page/provider';
import './component.card.css';

import { EmptyState } from '../common/no-data-warning/empty-state';
import { NoDataInfo } from '../common/no-data-warning/no-data-info';
import { useDataState } from '../data/provider';
import { useInView } from '../tools/inView.hook';
import { deepEqual } from '../tools/object';
import { useMobile } from '../context/mobile/mobile.provider';
import classNames from 'classnames';

const IN_VIEW_DELAY = 1000;

type Props = {
  showNoDataInfo: boolean;
  children: ReactNode;
  component: Component;
  settings: Params;
  updateComponentSettings: (updates: Params, shouldUpdateDsl?: boolean) => void;
  trackEvent: BasicTrackingFn;
  metadata?: SettingsProvider['metadata'];
  paramsForData: Params;
};
function ComponentCard({
  children,
  component,
  settings,
  updateComponentSettings,
  metadata,
  showNoDataInfo,
  paramsForData,
}: Props) {
  const [expanded, setExpanded] = useState<boolean>(true);
  const { data, isDataLoading } = useDataState();
  const threshold = component.contents?.fixed?.[0]?.type === CoreContents.leaderBoard ? 0 : 0.1;
  const { ref, inView } = useInView({ threshold, delay: IN_VIEW_DELAY });
  const currentView = settings?.dataViz?.[0] ?? metadata?.tracking?.elementName;
  const rememberedParams = useRef<Params>(null);
  const rememberedSettings = useRef<Params>(null);
  const rememberedView = useRef<string>(null);
  // sending in the title to get the featureGroups and comparisonPeriods for PageElementViewed
  const { trackPageElementViewed } = useTrackPageElementViewed(component?.heading?.title);
  const { someComponentsHaveData, dsl, dslLoading, contextLoading } = useCorePageState();

  const [hasPortalContent, setHasPortalContent] = useState(false);

  const dslOrContextLoading = dslLoading || contextLoading;

  const isLoading = isDataLoading || dslOrContextLoading;
  const { perform } = useUiAction();

  const { isMobile } = useMobile();

  const { name, heading, callsToAction = [], asCard = true, layout = 'vertical-list' } = component;

  const { Button, IconButton } = libActions;
  // flex grid needs figuring out - must use grid however to prevent component wider than page

  const cssLayoutMap = {
    'vertical-list': 'grid grid-cols-1 gap-5',
    'horizontal-list': 'grid grid-rows-1 grid-flow-row xl:grid-flow-col auto-cols-fr gap-4',
    'adaptive-grid': 'adaptive-grid max-w-6xl',
  };
  const defaultCss = 'grid grid-flow-row grid-flow-col auto-rows-min auto-cols-fr gap-4';

  const containerCss = cssLayoutMap[layout] || defaultCss;

  const hasHeading = heading?.title?.length > 0;

  const renderNoDataInfo = useCallback(() => {
    switch (component.noDataInfoType) {
      case 'ignore':
        return null;
      case 'info-alert':
        return <NoDataInfo />;
      case 'default':
      default:
        return <EmptyState />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showComponentCard = dsl?.settings?.enablePageLevelNoDataError ? someComponentsHaveData : true;

  const hasParamsChanged = !deepEqual(paramsForData, rememberedParams.current);
  const hasSettingsChanged = !deepEqual(settings, rememberedSettings.current);

  useEffect(() => {
    if (!isMobile) {
      setHasPortalContent(false);
      return;
    }

    const handleVisibilityChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ visible: boolean }>;
      const visible = !!customEvent.detail?.visible;
      setHasPortalContent(prev => (prev === visible ? prev : visible));
    };

    window.addEventListener('mobile-search-visibility', handleVisibilityChange);

    return () => {
      window.removeEventListener('mobile-search-visibility', handleVisibilityChange);
    };
  }, [isMobile]);

  useEffect(() => {
    if (paramsForData) {
      rememberedParams.current = paramsForData;
    }
  }, [paramsForData]);

  useEffect(() => {
    if (settings) {
      rememberedSettings.current = settings;
    }
  }, [settings]);

  useEffect(() => {
    /**
     *  if metadata.tracking(in bff settingsProvider for spesific view) is not set,
     *  metadata tracking is not set for core views because elementType and elementName dynamically changes by dataViz
     *  or updateOnContextChange is true, reset rememberedView
     **/
    if (hasParamsChanged) {
      rememberedView.current = 'willResetView';
      return;
    }
    const shouldResetView =
      (!metadata?.tracking || metadata?.tracking?.updateOnContextChange) && (isDataLoading || hasSettingsChanged);

    if (shouldResetView && rememberedView.current !== null) {
      rememberedView.current = null;
    }
  }, [metadata?.tracking, isDataLoading, hasParamsChanged, hasSettingsChanged]);

  useEffect(() => {
    const hasUpdatedView = currentView && rememberedView.current !== currentView;
    const isVisibleInDom = !isLoading && inView;
    const hasData = !!data;
    const hasSettings = !!settings;

    const shouldEventTrigger = hasUpdatedView && isVisibleInDom && hasData && hasSettings;

    if (shouldEventTrigger && rememberedView.current !== 'willResetView') {
      trackPageElementViewed(data, settings, metadata);
      rememberedView.current = currentView;
    }

    /* TODO: add more properties here depending on event trigger frequency */
  }, [isLoading, rememberedView, currentView, inView, settings, data, metadata, trackPageElementViewed]);

  return (
    <div
      className={classNames('component-card h-full w-full max-w-full flex flex-column self-stretch gap-2', {
        'bg-white rounded-2': asCard,
        'py-3': isMobile,
        'p-5': !isMobile,
      })}
      ref={ref}
    >
      {showComponentCard && hasHeading && (
        <div
          className={classNames('flex items-center', {
            'h-fit': isMobile,
            'h-10': !isMobile,
            relative: isMobile && hasPortalContent,
          })}
        >
          {isMobile && <div className="mobile-search-portal absolute z-50 w-full" />}
          {!hasPortalContent && (
            <div className="z-60">
              {heading.title && expanded && (
                <DynamicTitleElement title={heading.title} settings={settings} onChange={updateComponentSettings} />
              )}
              {heading.subTitle && <span className="block text-xs text-lighter">{heading.subTitle}</span>}
            </div>
          )}
          <div className="flex-grow flex items-center justify-end self-start">
            {expanded && (
              <div
                id={`${name}_header_actions`}
                className={classNames('flex justify-end w-full', {
                  'items-center': !isMobile,
                  'flex-wrap items-start pr-2': isMobile,
                })}
              />
            )}
          </div>
          {heading.isCollapsible && (
            <div className="self-start ml-4">
              <IconButton onClick={() => setExpanded(!expanded)} size="lg">
                {expanded ? <IconButton.icons.ChevronUp /> : <IconButton.icons.ChevronDown />}
              </IconButton>
            </div>
          )}
        </div>
      )}
      {expanded && (
        <>
          <div className="component-content grid flex-grow relative w-full max-w-full overflow-x-clip">
            {showComponentCard && showNoDataInfo && <div id={`no_data_warning`}>{renderNoDataInfo()}</div>}
            <div className={`relative w-full max-w-full ${containerCss}`}>{children}</div>
          </div>
          {showComponentCard && callsToAction.length > 0 && (
            <div className="mt-4 flex justify-end">
              {callsToAction.map((callToAction, idx) => (
                <Button
                  mode={callToAction.isPrimary === false ? 'secondary' : 'primary'}
                  onClick={() => perform(callToAction.action)}
                  key={`cta_${idx}`}
                >
                  <Button.Icon>
                    <Button.Icon.icons.Analyse />
                  </Button.Icon>
                  {callToAction.label}
                </Button>
              ))}
            </div>
          )}

          <div
            id={`${name}_footer`}
            className={classNames(`sticky bottom-0`, { 'rounded-bl-2 rounded-br-2': asCard, 'z-30': isMobile })}
          />
        </>
      )}
    </div>
  );
}
export { ComponentCard };
